import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, School as SchoolIcon, Building, MapPin, ArrowRight, RefreshCw,
  Loader2, Boxes, Coins, Sparkles, AlertTriangle, TrendingUp, LayoutGrid,
  Landmark, Route, ShieldAlert, Info, Trash2, PlusCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import Sidebar from '../components/Sidebar';
import { getSupplyExecutiveDashboard, getSupplySchoolDetails } from '../api';

const STATUS_COLORS = { delivered: '#34D399', allocated: '#9D8CFF' };
const STATUS_AR = { delivered: 'تم التوريد', allocated: 'مخصص' };
const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const pctFmt = (n) => (n == null ? 'غير متوفر' : `${Number(n).toFixed(1)}%`);

/**
 * TBC Supply Operations Command Center — v6.3
 * Executive home page. Consumes /api/supply/executive-dashboard.
 * Key design rule: **allocation is NOT delivery**. Delivery KPIs only appear
 * when the imported workbooks contain real delivery data (`hasDeliveryData=true`).
 */
export default function SupplyExecutiveDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getSupplyExecutiveDashboard()); }
    catch (e) { setError(e.response?.data?.detail || e.message || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div dir="rtl" className="supply-theme min-h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={30} className="animate-spin mx-auto mb-3" style={{ color: 'var(--se-violet-2)' }} />
            <div className="text-slate-400 text-[13px]">جاري تحميل مركز قيادة التوريدات...</div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div dir="rtl" className="supply-theme min-h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="se-panel p-6 max-w-md text-center">
            <AlertTriangle size={24} className="mx-auto mb-2" style={{ color: 'var(--se-red)' }} />
            <div className="text-[14px] font-bold mb-2">تعذر تحميل البيانات</div>
            <div className="text-slate-400 text-[12px] mb-4">{error}</div>
            <button className="se-btn se-btn-primary" onClick={load}><RefreshCw size={13} /> إعادة المحاولة</button>
          </div>
        </main>
      </div>
    );
  }

  const k = data.kpis;
  const emptyState = !data.hasData;

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="supply-exec-dashboard">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>TBC · SUPPLY OPERATIONS</div>
            <h1 className="se-title mt-1">مركز قيادة التوريدات</h1>
            <div className="se-subtitle">لوحة قيادة تنفيذية شاملة لمنظومة التوريدات · موردون · مدارس · أصناف · كميات مخصصة</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowManualEntry(true)} className="se-btn se-btn-ghost" data-testid="manual-entry-btn">
              <PlusCircle size={13} /> إدخال توريد يدوي
            </button>
            <button onClick={load} className="se-btn se-btn-ghost" data-testid="refresh-btn">
              <RefreshCw size={13} /> تحديث
            </button>
            <button onClick={() => nav('/supply/dynamic-import')} className="se-btn se-btn-primary" data-testid="import-btn">
              <Sparkles size={13} /> مركز الاستيراد
            </button>
          </div>
        </div>

        {showManualEntry && <ManualSupplyEntryModal onClose={() => setShowManualEntry(false)} onSaved={() => { setShowManualEntry(false); load(); }} />}

        {emptyState && <EmptyBanner onImport={() => nav('/supply/dynamic-import')} />}

        {/* ── Allocation KPI Row (always visible) ─── */}
        <section className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi hero label="إجمالي الموردين" value={numFmt(k.totalSuppliers)} unit="مورد" icon={Building}
              onClick={() => nav('/suppliers-list')} testid="kpi-total-suppliers" />
            <Kpi label="إجمالي المدارس" value={numFmt(k.totalSchools)} unit="مدرسة" icon={SchoolIcon} tint="#9D8CFF"
              onClick={() => nav('/schools')} testid="kpi-total-schools" />
            <Kpi label="إجمالي الأصناف" value={numFmt(k.totalItems)} unit="صنف" icon={Boxes} tint="#38D6C7"
              onClick={() => nav('/items-list')} testid="kpi-total-items" />
            <Kpi label="إجمالي الكميات المخصصة" value={numFmt(k.allocatedQuantity)} unit="قطعة" icon={LayoutGrid} tint="#9D8CFF"
              tooltip={`محسوب من ${numFmt(data.dataQuality.total_records)} سجل مستورد`} testid="kpi-allocated-qty" />
            <Kpi label="قيمة التوريدات" value={moneyFmt(k.allocatedValue)} unit="ريال · مخصص"
              icon={Coins} tint="#38D6C7"
              tooltip="محسوب فقط للسجلات التي تحتوي سعر وحدة" testid="kpi-allocated-value" />
            <Kpi label="المدارس المشمولة" value={numFmt(k.coveredSchools)} unit={`${numFmt(k.totalDepartments)} إدارة · ${numFmt(k.totalCities)} مدينة`}
              icon={Landmark} tint="#9D8CFF" testid="kpi-covered-schools" />
          </div>
        </section>

        {/* ── Delivery & Financial Completion KPIs ─── */}
        <section className="mb-6" data-testid="delivery-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="se-section-head">تقدم التوريد والإنجاز المالي</div>
            {data.hasDeliveryData
              ? <div className="se-pill se-pill-teal">بيانات توريد حقيقية</div>
              : <div className="se-pill" style={{ background: 'rgba(148,163,184,0.15)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.3)' }}>حالة التوريد غير موجودة في الملفات</div>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <Kpi label="الكمية الموردة فعلياً"
                 value={k.deliveredQuantity == null ? 'غير متوفر' : numFmt(k.deliveredQuantity)}
                 unit="قطعة" icon={Package}
                 tint={k.deliveredQuantity == null ? '#94A3B8' : '#34D399'} testid="kpi-delivered-qty" />
            <Kpi label="الكمية المتبقية"
                 value={k.remainingQuantity == null ? 'غير متوفر' : numFmt(k.remainingQuantity)}
                 unit="قطعة" icon={Route}
                 tint={k.remainingQuantity == null ? '#94A3B8' : '#FBBF24'} testid="kpi-remaining-qty" />
            <Kpi label="نسبة إنجاز التوريد"
                 value={k.completionRate == null ? 'غير متوفر' : pctFmt(k.completionRate)}
                 unit={k.completionRate == null ? 'يعتمد على حالة التوريد في الملف' : 'من الكميات المخصصة'}
                 icon={TrendingUp}
                 tint={k.completionRate == null ? '#94A3B8' : '#9D8CFF'} testid="kpi-supply-completion" />
            <Kpi label="القيمة المنجزة ماليًا"
                 value={k.financialCompletedValue == null ? 'غير متوفر' : moneyFmt(k.financialCompletedValue)}
                 unit={k.financialCompletedValue == null ? "يعتمد على «الإنجاز المالي للمدرسة»" : "ريال"}
                 icon={Coins}
                 tint={k.financialCompletedValue == null ? '#94A3B8' : '#38D6C7'} testid="kpi-financial-completed-value" />
            <Kpi label="نسبة الإنجاز المالي"
                 value={k.financialCompletion == null ? 'غير متوفر' : pctFmt(k.financialCompletion)}
                 unit={k.financialCompletion == null ? 'يعتمد على الحقل من الإكسل' : 'مستقل عن نسبة الكميات'}
                 icon={TrendingUp}
                 tint={k.financialCompletion == null ? '#94A3B8' : '#38D6C7'} testid="kpi-financial-completion" />
          </div>
        </section>

        {/* ── Insights + Data Quality strip ─── */}
        <section className="mb-6 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8">
            <SectionHead title="ذكاء التوريد" subtitle="رؤى محسوبة مباشرة من الملفات المستوردة — بدون افتراضات" />
            <SupplyIntelligencePanel insights={data.insights || []} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <SectionHead title="جودة البيانات" subtitle="Data Quality" />
            <DataQualityPanel dq={data.dataQuality} kpis={k} />
          </div>
        </section>

        {/* ── Map ─── */}
        <section className="mb-6">
          <SectionHead title="خريطة التوريد التنفيذية" subtitle={`${numFmt(data.map.length)} مدرسة بإحداثيات صالحة · اضغط على مدرسة لعرض ملف التوريد`} />
          <SupplyMap markers={data.map} hasDelivery={data.hasDeliveryData} onSelectSchool={(id) => setSelectedSchool(id)} />
        </section>

        {/* ── Supplier Portfolio ─── */}
        <section className="mb-6">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="se-section-head">أداء الموردين</div>
              <div className="se-section-sub">حصة كل مورد من إجمالي الكميات المخصصة · اضغط للتفاصيل</div>
            </div>
            <button className="se-btn se-btn-ghost" onClick={() => nav('/suppliers-list')}>عرض الكل <ArrowRight size={12} /></button>
          </div>
          <SupplierDonutRow suppliers={data.supplierPerformance} totalAllocated={k.allocatedQuantity} hasDelivery={data.hasDeliveryData} />
        </section>

        {/* ── Supplier Portfolio Cards ─── */}
        <section className="mb-6">
          <SectionHead title="محفظة الموردين" subtitle="مرتبة حسب قيمة التخصيص" />
          <SupplierPortfolio suppliers={data.supplierPerformance} hasDelivery={data.hasDeliveryData} onDeleted={load} />
        </section>

        {/* ── KPI Supply Map by Education Administration ─── */}
        <section className="mb-8">
          <SectionHead title="خارطة أداء التوريد حسب الإدارة التعليمية" subtitle="بيانات حقيقية فقط — استجابة كاملة للفلاتر" />
          <AdminSupplyMap suppliers={data.supplierPerformance || []} />
        </section>

        {/* ── Analytics ─── */}
        <section className="mb-8 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-7">
            <div className="se-panel p-4">
              <div className="se-section-head mb-1">التوريد حسب إدارة التعليم</div>
              <div className="se-section-sub mb-3">الكميات المخصصة</div>
              {(data.byDepartment || []).length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.byDepartment.slice(0, 12)} layout="vertical" margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" tick={{ fill: '#8F8AA6', fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fill: '#C7C3DB', fontSize: 11 }} />
                    <RTooltip contentStyle={{ background: '#1B1A25', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, direction: 'rtl', color: '#F1EEFB' }}
                      formatter={(v) => [Number(v).toLocaleString('en-US'), 'كمية مخصصة']} />
                    <Bar dataKey="allocated_qty" name="مخصص" fill="url(#supplyGradientV)" radius={[0, 4, 4, 0]} />
                    <defs>
                      <linearGradient id="supplyGradientV" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#6D5DFB" />
                        <stop offset="100%" stopColor="#9D8CFF" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="se-panel p-4">
              <div className="se-section-head mb-1">الأصناف الأعلى تخصيصاً</div>
              <div className="se-section-sub mb-3">أعلى 10 أصناف من حيث الكمية</div>
              {(data.topItems || []).length === 0 ? <EmptyChart /> : (
                <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                  <table className="se-table">
                    <thead><tr><th className="w-8">#</th><th>الصنف</th><th className="text-center">الكمية</th><th className="text-center">المدارس</th></tr></thead>
                    <tbody>
                      {data.topItems.slice(0, 10).map((it, i) => (
                        <tr key={i} data-testid={`top-item-${i}`}>
                          <td className="text-slate-500 tabular-nums">{i + 1}</td>
                          <td className="truncate max-w-[280px]" title={it.name}>{it.name}</td>
                          <td className="text-center tabular-nums">{numFmt(it.qty)}</td>
                          <td className="text-center tabular-nums">{numFmt(it.schools)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="text-center text-slate-500 text-[11px] pt-4">
          TBC Supply Operations Command Center · v6.3 · © 2026
        </div>

        {selectedSchool && <SchoolSupplyModal schoolId={selectedSchool} onClose={() => setSelectedSchool(null)} />}
      </main>
    </div>
  );
}

/* ── Reusable pieces ─────────────────────────────────────── */
function Kpi({ label, value, unit, icon: Icon, tint, hero, onClick, testid, tooltip }) {
  return (
    <div className={`se-kpi ${hero ? 'se-kpi-hero' : ''}`} onClick={onClick} data-testid={testid} title={tooltip || undefined}>
      <div className="flex items-center justify-between">
        <div className="se-kpi-label">{label}</div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: hero ? 'rgba(255,255,255,0.15)' : `${tint || '#6D5DFB'}22`,
                     border: hero ? '1px solid rgba(255,255,255,0.22)' : `1px solid ${tint || '#6D5DFB'}44` }}>
            <Icon size={15} strokeWidth={2} style={{ color: hero ? '#FFFFFF' : (tint || '#9D8CFF') }} />
          </div>
        )}
      </div>
      <div className="se-kpi-value">{value}</div>
      {unit && <div className="se-kpi-unit">{unit}</div>}
    </div>
  );
}

function SectionHead({ title, subtitle }) {
  return (
    <div className="mb-3">
      <div className="se-section-head">{title}</div>
      <div className="se-section-sub">{subtitle}</div>
    </div>
  );
}

function EmptyBanner({ onImport }) {
  return (
    <div className="se-panel p-6 mb-6 flex items-center gap-4" data-testid="empty-banner"
      style={{ background: 'linear-gradient(135deg, rgba(109,93,251,0.12) 0%, rgba(56,214,199,0.08) 100%)', border: '1px solid rgba(109,93,251,0.32)' }}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(109,93,251,0.2)', border: '1px solid rgba(109,93,251,0.4)' }}>
        <Package size={22} style={{ color: '#B7ADFF' }} />
      </div>
      <div className="flex-1">
        <div className="text-[15px] font-bold" style={{ color: '#F1EEFB' }}>لا توجد بيانات توريد بعد</div>
        <div className="text-[12px] text-slate-400 mt-1">ابدأ برفع ملف Excel من أحد الموردين — سيتم تحليل الهيكل تلقائياً وتحديث لوحة القيادة.</div>
      </div>
      <button className="se-btn se-btn-primary" onClick={onImport}><Sparkles size={13} /> رفع ملف الآن</button>
    </div>
  );
}

function EmptyChart() {
  return <div className="se-empty py-12" data-testid="empty-chart">لا توجد بيانات لعرضها</div>;
}

function SupplyIntelligencePanel({ insights }) {
  if (!insights || insights.length === 0) {
    return <div className="se-panel p-6"><div className="se-empty">لم يتم توليد رؤى — قم برفع ملفات التوريد لعرض التحليلات.</div></div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="intelligence-panel">
      {insights.map((i, ix) => (
        <div key={ix} className="se-panel p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: i.warning ? 'rgba(251,191,36,0.14)' : 'rgba(109,93,251,0.16)',
                     border: `1px solid ${i.warning ? 'rgba(251,191,36,0.32)' : 'rgba(109,93,251,0.32)'}` }}>
            {i.warning ? <ShieldAlert size={15} style={{ color: '#FBBF24' }} /> : <Info size={15} style={{ color: '#B7ADFF' }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] text-slate-400 font-medium">{i.label}</div>
            <div className="text-[15px] font-bold truncate mt-1" style={{ color: i.warning ? '#FCD34D' : '#F1EEFB' }} title={i.value}>{i.value}</div>
            <div className="text-[11px] text-slate-500 mt-1">{i.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DataQualityPanel({ dq, kpis }) {
  const items = [
    { label: 'المدارس بإحداثيات صالحة', value: `${numFmt(dq.schools_with_coords)} / ${numFmt(dq.schools_with_coords + dq.schools_missing_coords)}`, warning: dq.schools_missing_coords > 0 },
    { label: 'سجلات بدون سعر وحدة',      value: numFmt(dq.missing_unit_price), warning: dq.missing_unit_price > 0 },
    { label: 'سجلات بدون كمية',           value: numFmt(dq.missing_quantity),   warning: dq.missing_quantity > 0 },
    { label: 'سجلات بدون رقم مدرسة',     value: numFmt(dq.missing_school_id),  warning: dq.missing_school_id > 0 },
  ];
  return (
    <div className="se-panel p-4" data-testid="data-quality-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[13px] font-bold" style={{ color: '#F1EEFB' }}>مؤشر الجودة</div>
        <div className="text-[28px] font-extrabold tabular-nums" style={{ color: dq.health_score >= 90 ? '#34D399' : dq.health_score >= 70 ? '#FBBF24' : '#F87171' }}>
          {pctFmt(dq.health_score)}
        </div>
      </div>
      <div className="se-progress mb-4"><div style={{ width: `${dq.health_score}%` }} /></div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between text-[11.5px]">
            <span className="text-slate-400">{it.label}</span>
            <span className="tabular-nums font-semibold" style={{ color: it.warning ? '#FCD34D' : '#C7C3DB' }}>{it.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupplyMap({ markers, hasDelivery, onSelectSchool }) {
  const valid = (markers || []).filter((m) => !isNaN(m.lat) && !isNaN(m.lng) && m.lat && m.lng);
  if (valid.length === 0) {
    return <div className="se-panel p-3" style={{ height: 200 }}><div className="se-empty h-full flex items-center justify-center">لم يتم اكتشاف مدارس بإحداثيات صالحة</div></div>;
  }
  const center = [valid[0].lat, valid[0].lng];
  return (
    <div className="se-panel p-3" style={{ height: 460 }}>
      <MapContainer center={center} zoom={7} scrollWheelZoom style={{ height: '100%', width: '100%', borderRadius: 12, background: '#0F0E15' }}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          className="se-map-tiles"
        />
        {valid.map((m) => (
          <CircleMarker
            key={m.school_id}
            center={[m.lat, m.lng]}
            radius={5}
            pathOptions={{
              color: STATUS_COLORS[m.status] || '#9D8CFF',
              fillColor: STATUS_COLORS[m.status] || '#9D8CFF',
              fillOpacity: 0.7, weight: 1.5,
            }}
            eventHandlers={{ click: () => onSelectSchool && onSelectSchool(m.school_id) }}
          >
            <Popup>
              <div dir="rtl" style={{ minWidth: 220 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{m.school_name || m.school_id}</div>
                <div style={{ fontSize: 11, color: '#64748B' }}>الرقم الوزاري: {m.school_id}</div>
                {m.supplier && <div style={{ fontSize: 11, color: '#64748B' }}>المورد: {m.supplier}</div>}
                {m.region && <div style={{ fontSize: 11, color: '#64748B' }}>المنطقة: {m.region}</div>}
                <div style={{ fontSize: 11, color: STATUS_COLORS[m.status] || '#6D5DFB', fontWeight: 600, marginTop: 4 }}>
                  {STATUS_AR[m.status] || m.status}
                </div>
                <button onClick={() => onSelectSchool && onSelectSchool(m.school_id)}
                  style={{ marginTop: 8, background: '#6D5DFB', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', width: '100%' }}>
                  فتح ملف المدرسة
                </button>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
      <div className="flex items-center gap-4 mt-3 text-[11.5px]" data-testid="map-legend">
        {hasDelivery && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS.delivered }} /> تم التوريد</span>}
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS.allocated }} /> مخصص</span>
        <span className="text-slate-500 mr-auto">{valid.length.toLocaleString('en-US')} مدرسة على الخريطة</span>
      </div>
    </div>
  );
}

const DONUT_COLORS = ['#6D5DFB', '#9D8CFF', '#B7ADFF', '#38D6C7', '#5EE3D6', '#8B7DFF', '#7B67FF'];

function SupplierDonutRow({ suppliers, totalAllocated, hasDelivery }) {
  const nav = useNavigate();
  const list = (suppliers || []).slice(0, 6);
  if (list.length === 0) return <div className="se-panel p-8"><div className="se-empty">لا توجد بيانات لعرض الحصص</div></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-donut-row">
      {list.map((s, i) => {
        const share = totalAllocated ? (s.allocated_qty / totalAllocated) * 100 : 0;
        const color = DONUT_COLORS[i % DONUT_COLORS.length];
        // If real delivery data exists, show delivered vs remaining; else show share of total allocation.
        const hasReal = hasDelivery && s.delivered_qty != null && s.allocated_qty > 0;
        const donutData = hasReal
          ? [
              { name: 'مورد', value: s.delivered_qty },
              { name: 'متبقي', value: Math.max(0, s.allocated_qty - s.delivered_qty) },
            ]
          : [
              { name: 'حصة المورد', value: s.allocated_qty },
              { name: 'باقي الموردين', value: Math.max(0, totalAllocated - s.allocated_qty) },
            ];
        const centerValue = hasReal
          ? `${(s.completion || 0).toFixed(0)}%`
          : `${share.toFixed(1)}%`;
        const centerLabel = hasReal ? 'إنجاز' : 'الحصة';
        return (
          <div key={i} className="se-panel-hi cursor-pointer transition-transform hover:-translate-y-1"
            data-testid={`supplier-donut-${i}`}
            onClick={() => nav('/suppliers/' + encodeURIComponent(s.name))}
            style={{ padding: 18 }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
                <Building size={14} style={{ color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold truncate" style={{ color: '#F1EEFB' }} title={s.name}>{s.name}</div>
                <div className="text-[10.5px] text-slate-400">
                  {numFmt(s.schools)} مدرسة · {numFmt(s.items)} صنف
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" cx="50%" cy="50%"
                      innerRadius={38} outerRadius={54} strokeWidth={0} paddingAngle={2} startAngle={90} endAngle={-270}>
                      <Cell fill={color} />
                      <Cell fill="rgba(255,255,255,0.06)" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', pointerEvents: 'none' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{centerValue}</div>
                  <div style={{ fontSize: 9.5, color: '#8F8AA6', marginTop: 2 }}>{centerLabel}</div>
                </div>
              </div>
              <div className="flex-1 space-y-1.5 text-[11px]">
                <div className="flex justify-between"><span className="text-slate-400">الكمية المخصصة</span><span className="tabular-nums font-semibold" style={{ color: '#F1EEFB' }}>{numFmt(s.allocated_qty)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">القيمة</span><span className="tabular-nums font-semibold" style={{ color: '#38D6C7' }}>{moneyFmt(s.allocated_value)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">التغطية</span><span className="tabular-nums" style={{ color: '#C7C3DB' }}>{numFmt(s.depts || 0)} إدارة · {numFmt(s.cities || 0)} مدينة</span></div>
                <div className="mt-2 pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="text-[9.5px] text-slate-500">{hasReal ? 'إنجاز فعلي (موردة/مخصصة)' : `حصة من إجمالي ${numFmt(totalAllocated)} قطعة`}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SupplierPortfolio({ suppliers, hasDelivery, onDeleted }) {
  const nav = useNavigate();
  const list = (suppliers || []).slice(0, 12);
  const [deleting, setDeleting] = React.useState(null);
  const handleDelete = async (e, name) => {
    e.stopPropagation();
    const confirmMsg = `هل أنت متأكد من حذف بيانات «${name}»؟\nسيتم حذف بيانات هذا المورد فقط ولن تتأثر بيانات الموردين الآخرين.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(name);
    try {
      const { deleteSupplier } = await import('../api');
      await deleteSupplier(name);
      onDeleted?.(name);
    } finally { setDeleting(null); }
  };
  if (list.length === 0) return <div className="se-panel p-8"><div className="se-empty">لا يوجد موردون</div></div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-portfolio">
      {list.map((s, i) => (
        <div key={i} className="se-panel-hi p-5 cursor-pointer transition-transform hover:-translate-y-1 relative"
          data-testid={`supplier-card-${i}`}
          onClick={() => nav('/suppliers/' + encodeURIComponent(s.name))}
          style={{ padding: 20 }}>
          <button
            data-testid={`supplier-delete-${i}`}
            title={`حذف بيانات ${s.name} فقط — لن تتأثر بيانات الموردين الآخرين`}
            onClick={(e) => handleDelete(e, s.name)}
            disabled={deleting === s.name}
            className="absolute top-3 left-3 h-7 px-2 rounded-md flex items-center gap-1 text-[10.5px] font-semibold hover:bg-red-500/25 disabled:opacity-50"
            style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.4)', color: '#FCA5A5' }}>
            <Trash2 size={11} /> حذف المورد
          </button>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(109,93,251,0.18)', border: '1px solid rgba(109,93,251,0.36)' }}>
              <Building size={18} style={{ color: '#B7ADFF' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold truncate" style={{ color: '#F1EEFB' }} title={s.name}>{s.name}</div>
              <div className="text-slate-400 text-[11px] mt-0.5">
                {numFmt(s.schools)} مدرسة · {numFmt(s.items)} صنف · {numFmt(s.depts)} إدارة
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <MiniTile label="الكمية المخصصة" value={numFmt(s.allocated_qty)} unit="قطعة" />
            <MiniTile label="القيمة" value={moneyFmt(s.allocated_value)} unit="ريال" tint="#38D6C7" />
          </div>
          {/* Supply completion (qty-based) */}
          {s.completion != null ? (
            <>
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-slate-400">نسبة إنجاز التوريد</span>
                <span className="font-bold tabular-nums" style={{ color: s.completion >= 80 ? '#34D399' : s.completion >= 40 ? '#FBBF24' : '#F87171' }}>
                  {pctFmt(s.completion)}
                </span>
              </div>
              <div className="se-progress mb-1"><div style={{ width: `${Math.min(100, s.completion)}%` }} /></div>
              <div className="text-[10.5px] text-slate-500 mb-2">
                موردة {numFmt(s.delivered_qty)} · متبقية {numFmt(s.remaining_qty)}
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-[10.5px] mt-1 mb-2 flex items-center gap-1">
              <Info size={11} /> نسبة إنجاز التوريد: غير متوفر
            </div>
          )}
          {/* Financial completion — INDEPENDENT of qty completion */}
          {s.financial_completion != null ? (
            <div className="flex items-center justify-between text-[11px] pt-1 border-t border-white/5">
              <span className="text-slate-400">نسبة الإنجاز المالي</span>
              <span className="font-bold tabular-nums" style={{ color: '#38D6C7' }}>
                {pctFmt(s.financial_completion)}
              </span>
            </div>
          ) : (
            <div className="text-slate-500 text-[10.5px] pt-1 border-t border-white/5 flex items-center gap-1">
              <Info size={11} /> نسبة الإنجاز المالي: غير متوفر
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MiniTile({ label, value, unit, tint = '#9D8CFF' }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="text-[15px] font-extrabold tabular-nums mt-0.5" style={{ color: tint }}>{value}</div>
      {unit && <div className="text-[9.5px] text-slate-500 mt-0.5">{unit}</div>}
    </div>
  );
}

function SchoolSupplyModal({ schoolId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getSupplySchoolDetails(schoolId).then((d) => { if (alive) { setData(d); setLoading(false); } });
    return () => { alive = false; };
  }, [schoolId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,9,16,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="school-supply-modal">
      <div className="se-panel-hi max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: 24 }}>
        {loading ? (
          <div className="p-10 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : !data?.found ? (
          <div className="p-10 text-center">
            <div className="font-bold" style={{ color: '#F1EEFB' }}>لا توجد بيانات توريد لهذه المدرسة</div>
            <button className="se-btn se-btn-primary mt-4" onClick={onClose}>إغلاق</button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold tracking-wider" style={{ color: '#B7ADFF' }}>SCHOOL SUPPLY PROFILE</div>
                <div className="text-[20px] font-bold mt-1" style={{ color: '#F1EEFB' }}>{data.school_name || 'غير متوفر'}</div>
                <div className="text-slate-400 text-[12px] mt-1">
                  الرقم الوزاري: <span className="font-semibold">{data.school_id || 'غير متوفر'}</span>
                  <span className="mx-2">·</span>
                  المنطقة: {data.region || 'غير متوفر'}
                  {data.stage && <><span className="mx-2">·</span>{data.stage}</>}
                </div>
              </div>
              <button onClick={onClose} className="se-btn se-btn-ghost">إغلاق</button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <MiniTile label="عدد الأصناف" value={numFmt(data.summary.items_count)} />
              <MiniTile label="الكمية المخصصة" value={numFmt(data.summary.total_quantity)} unit="قطعة" tint="#9D8CFF" />
              <MiniTile label="القيمة المخصصة" value={moneyFmt(data.summary.total_value)} unit="ريال" tint="#38D6C7" />
              {data.summary.delivered_quantity > 0 ? (
                <MiniTile label="الموردة فعلياً" value={numFmt(data.summary.delivered_quantity)} unit={`${pctFmt(data.summary.completion)}`} tint="#34D399" />
              ) : (
                <MiniTile label="التوريد الفعلي" value="غير متوفر" tint="#8F8AA6" />
              )}
            </div>

            <div className="text-[14px] font-bold mb-2" style={{ color: '#F1EEFB' }}>تفاصيل الأصناف</div>
            <div className="se-panel overflow-x-auto" style={{ padding: 0 }}>
              <table className="se-table">
                <thead>
                  <tr><th>الصنف</th><th>المورد</th><th className="text-center">الكمية المخصصة</th><th className="text-center">سعر الوحدة</th><th className="text-center">القيمة</th></tr>
                </thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i}>
                      <td>{it.item || '—'}</td>
                      <td className="text-slate-400">{it.company || '—'}</td>
                      <td className="text-center tabular-nums">{numFmt(it.quantity)}</td>
                      <td className="text-center tabular-nums">{numFmt(it.unit_price)}</td>
                      <td className="text-center tabular-nums">{moneyFmt(it.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Manual Supply Entry Modal ────────────────────────────────────────
function ManualSupplyEntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    company: '', item: '', quantity: '', unit_price: '',
    school_name: '', school_id: '', governorate: '', region: '',
    stage: '', gender: '', coords: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.company || !form.item || !form.quantity || !form.unit_price) {
      setErr('يرجى تعبئة المورد والصنف والكمية والسعر'); return;
    }
    if (!form.school_name && !form.school_id) {
      setErr('يرجى إدخال اسم المدرسة أو الرقم الوزاري'); return;
    }
    setSaving(true);
    try {
      const { addManualSupplyRecord } = await import('../api');
      await addManualSupplyRecord({
        ...form,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
      });
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ background: 'rgba(6,5,15,0.85)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <form dir="rtl" onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="manual-entry-modal"
            className="w-[min(760px,96vw)] rounded-2xl overflow-hidden"
            style={{ background: '#0F0E19', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="text-[16px] font-bold" style={{ color: '#F1EEFB' }}>إدخال توريد يدوي</div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="manual-close">✕</button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="المورد *"><input required data-testid="me-company" value={form.company} onChange={setField('company')} className="se-input w-full" /></Field>
          <Field label="الصنف *"><input required data-testid="me-item" value={form.item} onChange={setField('item')} className="se-input w-full" /></Field>
          <Field label="الكمية *"><input required type="number" data-testid="me-qty" value={form.quantity} onChange={setField('quantity')} className="se-input w-full" /></Field>
          <Field label="سعر الوحدة *"><input required type="number" data-testid="me-price" value={form.unit_price} onChange={setField('unit_price')} className="se-input w-full" /></Field>
          <Field label="اسم المدرسة"><input data-testid="me-school" value={form.school_name} onChange={setField('school_name')} className="se-input w-full" /></Field>
          <Field label="الرقم الوزاري"><input data-testid="me-mid" value={form.school_id} onChange={setField('school_id')} className="se-input w-full" /></Field>
          <Field label="الإدارة التعليمية"><input data-testid="me-gov" value={form.governorate} onChange={setField('governorate')} className="se-input w-full" /></Field>
          <Field label="المدينة"><input data-testid="me-region" value={form.region} onChange={setField('region')} className="se-input w-full" /></Field>
          <Field label="المرحلة"><input data-testid="me-stage" value={form.stage} onChange={setField('stage')} className="se-input w-full" /></Field>
          <Field label="الإحداثيات (lat,lng)"><input data-testid="me-coords" value={form.coords} onChange={setField('coords')} className="se-input w-full" placeholder="مثال: 17.05,43.04" /></Field>
        </div>
        {err && <div data-testid="manual-error" className="mx-5 mb-2 p-2 rounded text-[12px]" style={{ background: '#3F1D1D', color: '#FCA5A5' }}>{err}</div>}
        <div className="px-5 py-4 border-t border-white/10 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="se-btn se-btn-ghost">إلغاء</button>
          <button type="submit" disabled={saving} className="se-btn se-btn-primary" data-testid="manual-save">
            {saving ? 'جاري الحفظ...' : 'حفظ التوريد'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10.5px] uppercase tracking-widest text-slate-400">{label}</span>
      {children}
    </label>
  );
}

// ── KPI Supply Map (by Education Administration) ─────────────────────
function AdminSupplyMap({ suppliers }) {
  const [supplier, setSupplier] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      const { getMapByAdmin } = await import('../api');
      const d = await getMapByAdmin(supplier ? { supplier } : {});
      if (alive) { setData(d); setLoading(false); setSelected(null); }
    })();
    return () => { alive = false; };
  }, [supplier]);
  const markers = data?.markers || [];
  const totals = data?.totals || {};
  const center = markers.length ? [markers[0].lat, markers[0].lng] : [24, 45];
  return (
    <div className="se-panel p-3" data-testid="admin-supply-map">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-[11.5px] text-slate-400">فلترة بالمورد:</label>
        <select data-testid="map-supplier-filter" value={supplier} onChange={(e) => setSupplier(e.target.value)} className="se-input" style={{ padding: '6px 10px' }}>
          <option value="">جميع الموردين</option>
          {suppliers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        <div className="mr-auto text-[11.5px] text-slate-400 flex gap-3 flex-wrap">
          <span>إدارات: <b style={{ color: '#F1EEFB' }}>{numFmt(totals.administrations || 0)}</b></span>
          <span>بإحداثيات: <b style={{ color: '#38D6C7' }}>{numFmt(totals.administrations_with_coords || 0)}</b></span>
          <span>بدون إحداثيات: <b style={{ color: '#F59E0B' }}>{numFmt(totals.administrations_missing_coords || 0)}</b></span>
          <span>القيمة: <b style={{ color: '#9D8CFF' }}>{moneyFmt(totals.allocated_value || 0)} ر.س</b></span>
          <span>الإنجاز: <b style={{ color: totals.has_delivery_data ? '#34D399' : '#94A3B8' }}>
            {totals.has_delivery_data ? '' : 'غير متوفر — لا تحتوي الملفات المصدر على حالة توريد'}
          </b></span>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 lg:col-span-8" style={{ height: 380 }}>
          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin" style={{ color: '#9D8CFF' }} /></div>
          ) : markers.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-500 text-[12px]">لا توجد إحداثيات للإدارات ضمن هذا الفلتر</div>
          ) : (
            <MapContainer center={center} zoom={5} scrollWheelZoom style={{ height: '100%', width: '100%', borderRadius: 12, background: '#0F0E15' }} data-testid="admin-map-container">
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" className="se-map-tiles" />
              {markers.map((m) => (
                <CircleMarker key={m.administration} center={[m.lat, m.lng]}
                              radius={Math.min(24, 6 + Math.log10(Math.max(1, m.allocated_value)) * 2)}
                              pathOptions={{ color: '#9D8CFF', fillColor: '#6D5DFB', fillOpacity: 0.55, weight: 2 }}
                              eventHandlers={{ click: () => setSelected(m) }}>
                  <Popup>
                    <div style={{ direction: 'rtl' }}>
                      <b>{m.administration}</b><br />
                      {numFmt(m.school_count)} مدرسة · {numFmt(m.supplier_count)} مورد<br />
                      {moneyFmt(m.allocated_value)} ر.س<br />
                      نسبة الإنجاز: {m.completion == null ? 'غير متوفر' : pctFmt(m.completion)}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          )}
        </div>
        <div className="col-span-12 lg:col-span-4 se-panel-hi p-3" style={{ background: 'rgba(255,255,255,0.02)' }} data-testid="admin-map-detail">
          {selected ? (
            <>
              <div className="text-[11px] text-slate-400">تفاصيل الإدارة التعليمية</div>
              <div className="text-[18px] font-bold mt-1" style={{ color: '#F1EEFB' }}>{selected.administration}</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-[12px]">
                <Stat label="مدارس" value={numFmt(selected.school_count)} />
                <Stat label="مدن" value={numFmt(selected.city_count)} />
                <Stat label="أصناف" value={numFmt(selected.item_count)} />
                <Stat label="موردون" value={numFmt(selected.supplier_count)} />
                <Stat label="الكمية المخصصة" value={numFmt(selected.allocated_qty)} tint="#9D8CFF" />
                <Stat label="القيمة (ر.س)" value={moneyFmt(selected.allocated_value)} tint="#38D6C7" />
                <Stat label="الكمية الموردة" value={selected.delivered_qty == null ? 'غير متوفر' : numFmt(selected.delivered_qty)} tint={selected.delivered_qty == null ? '#94A3B8' : '#34D399'} />
                <Stat label="نسبة الإنجاز" value={selected.completion == null ? 'غير متوفر' : pctFmt(selected.completion)} tint={selected.completion == null ? '#94A3B8' : '#34D399'} />
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-[12px]">اضغط على أي إدارة على الخارطة لعرض تفاصيلها</div>
          )}
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, tint = '#F1EEFB' }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="text-[14px] font-bold tabular-nums" style={{ color: tint }}>{value}</div>
    </div>
  );
}
